import { createPortal } from "react-dom"
import { useState } from "react"
import { Check, Copy, Link2, X } from "lucide-react"

/**
 * Zeigt einen frisch erzeugten Einladungslink — genau einmal (02.11.2026).
 *
 * Statt ein Passwort auszudenken und es per Chat zu schicken, bekommt die
 * neue Person diesen Link. Darüber setzt sie Passwort und zweiten Faktor
 * selbst. Der Link gilt einmal und 48 Stunden; ein neuer macht den alten
 * ungültig.
 *
 * 🔴 Der Link steht nur in dieser einen Antwort — gespeichert ist allein sein
 * Hash. Wer ihn verliert, erzeugt einfach einen neuen.
 */

export type Einladung = {
  username: string
  link: string
  gueltig_bis: string
  stunden: number
  gesendet_an?: string | null
  mail_fehler?: string | null
}

export function EinladungDialog({ daten, onClose }: {
  daten: Einladung
  onClose: () => void
}) {
  const [kopiert, setKopiert] = useState(false)
  const bis = new Date(daten.gueltig_bis).toLocaleString("de-DE", {
    weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  })

  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(daten.link)
      setKopiert(true)
      setTimeout(() => setKopiert(false), 2000)
    } catch {
      /* Ohne Zwischenablage bleibt der Link zum Markieren. */
    }
  }

  // 🔑 createPortal: sonst schneidet ein `overflow: hidden` weiter oben im
  // Baum den Dialog ab — dieselbe Falle wie bei ZweiFaktorDialog.
  return createPortal(
    <div
      className="pp-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Einladungslink"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(15,23,42,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div style={{
        width: "100%", maxWidth: 480, borderRadius: 10, padding: 20,
        background: "var(--bg-card)", border: "1px solid var(--border-color)",
        boxShadow: "0 18px 40px rgba(0,0,0,0.28)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Link2 size={17} />
          <strong style={{ fontSize: 15 }}>Einladungslink für „{daten.username}“</strong>
          <button type="button" onClick={onClose} aria-label="Schließen"
                  style={{ marginLeft: "auto", background: "none", border: "none",
                           cursor: "pointer", color: "var(--text-muted)" }}>
            <X size={16} />
          </button>
        </div>

        {daten.gesendet_an && (
          <p style={{ fontSize: 12.5, color: "#059669", marginBottom: 10 }}>
            Per Mail an {daten.gesendet_an} geschickt (von kontakt@werkxpartner.de).
          </p>
        )}
        {daten.mail_fehler && (
          <p style={{ fontSize: 12.5, color: "var(--destructive)", marginBottom: 10 }}>
            {daten.mail_fehler}
          </p>
        )}

        <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 12 }}>
          {daten.gesendet_an
            ? "Hier zur Sicherheit noch einmal der Link. "
            : `Schick diesen Link an ${daten.username}. `}
          Damit legt {daten.username} Passwort und zweiten Faktor selbst fest. Er gilt <strong>einmal</strong> und
          bis <strong>{bis}</strong>.
        </p>

        <div style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 12.5, wordBreak: "break-all", userSelect: "all",
          padding: "10px 12px", borderRadius: 7, marginBottom: 8,
          border: "1px solid var(--border-color)", background: "var(--bg)",
        }}>
          {daten.link}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
          <button type="button" onClick={kopieren}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5,
                           padding: "6px 10px", borderRadius: 6, cursor: "pointer",
                           border: "1px solid var(--border-color)", background: "var(--bg-card)",
                           color: "var(--text)" }}>
            {kopiert ? <><Check size={13} /> Kopiert</> : <><Copy size={13} /> Link kopieren</>}
          </button>
        </div>

        <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14 }}>
          Der Link wird nur jetzt angezeigt. Geht er verloren, erzeugst du einfach
          einen neuen — der alte gilt dann nicht mehr. Ein bisheriges Passwort
          bleibt gültig, bis der Link benutzt wurde.
        </p>

        <button type="button" onClick={onClose}
                style={{ fontSize: 13, fontWeight: 500, padding: "7px 14px", borderRadius: 6,
                         border: "none", background: "#3C3489", color: "#fff", cursor: "pointer" }}>
          Fertig
        </button>
      </div>
    </div>,
    document.body,
  )
}
